import { makeObservable, observable, action, runInAction, toJS } from 'mobx';
import {
  getAllUsers,
  approveUser,
  rejectUser,
  deactivateUser,
  updateUserRole
} from '../helpers/api';

class UsersManagementStore {
  _users = [];
  _pendingUsersCount = 0;
  _isLoading = false;
  _error = null;
  _successMessage = null;

  constructor() {
    makeObservable(this, {
      _users: observable,
      _pendingUsersCount: observable,
      _isLoading: observable,
      _error: observable,
      _successMessage: observable,

      loadUsers: action,
      approveUser: action,
      rejectUser: action,
      deactivateUser: action,
      updateUserRole: action,
      setError: action,
      setSuccess: action,
      clearMessages: action
    });
  }

  // Getters
  get users() {
    return toJS(this._users);
  }

  get pendingUsersCount() {
    return this._pendingUsersCount;
  }

  get isLoading() {
    return this._isLoading;
  }

  get error() {
    return this._error;
  }

  get successMessage() {
    return this._successMessage;
  }

  // Actions
  setError(error) {
    this._error = error;
  }

  setSuccess(message) {
    this._successMessage = message;
  }

  clearMessages() {
    this._error = null;
    this._successMessage = null;
  }

  async loadUsers(filters = {}) {
    this._isLoading = true;
    this._error = null;

    try {
      const data = await getAllUsers(filters);
      runInAction(() => {
        this._users = data.users || [];
        this._pendingUsersCount = data.pendingCount || data.users.filter(u => !u.approved).length;
        this._isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this._error = 'Erro ao carregar usuários';
        this._isLoading = false;
      });
      console.error('Failed to load users:', err);
    }
  }

  async approveUser(userId, role, employeeSettings) {
    this._isLoading = true;
    this._error = null;

    try {
      await approveUser(userId, { role, employee: employeeSettings });
      runInAction(() => {
        this._successMessage = 'Usuário aprovado com sucesso';
        this._isLoading = false;
      });
      await this.loadUsers();
      return true;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Erro ao aprovar usuário';
        this._isLoading = false;
      });
      console.error('Failed to approve user:', err);
      return false;
    }
  }

  async rejectUser(userId) {
    this._isLoading = true;
    this._error = null;

    try {
      await rejectUser(userId);
      runInAction(() => {
        this._successMessage = 'Usuário rejeitado';
        this._isLoading = false;
      });
      await this.loadUsers();
      return true;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Erro ao rejeitar usuário';
        this._isLoading = false;
      });
      console.error('Failed to reject user:', err);
      return false;
    }
  }

  async deactivateUser(userId) {
    this._isLoading = true;
    this._error = null;

    try {
      await deactivateUser(userId);
      runInAction(() => {
        this._successMessage = 'Usuário desativado';
        this._isLoading = false;
      });
      await this.loadUsers();
      return true;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Erro ao desativar usuário';
        this._isLoading = false;
      });
      console.error('Failed to deactivate user:', err);
      return false;
    }
  }

  async updateUserRole(userId, role, employeeSettings) {
    this._isLoading = true;
    this._error = null;

    try {
      await updateUserRole(userId, { role, employee: employeeSettings });
      runInAction(() => {
        this._successMessage = 'Função do usuário atualizada';
        this._isLoading = false;
      });
      await this.loadUsers();
      return true;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Erro ao atualizar função';
        this._isLoading = false;
      });
      console.error('Failed to update user role:', err);
      return false;
    }
  }
}

const usersManagementStore = new UsersManagementStore();
export default usersManagementStore;